import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { RootState } from "../../redux/store";

export interface PaymentDetails {
  bank?: string;
  bank_name?: string;
  account_number?: string;
  account_name?: string;
  amount?: number;
  reference?: string;
  accountExpiry?: string;
}

interface PurchaseState {
  loading: boolean;
  error: string | null;
  paymentDetails: PaymentDetails | null;
  transactionStatus: string | null;
  polling: boolean;
}

const initialState: PurchaseState = {
  loading: false,
  error: null,
  paymentDetails: null,
  transactionStatus: null,
  polling: false,
};

const API_URL = process.env.REACT_APP_API_URL;

export const initiatePurchase = createAsyncThunk<
  PaymentDetails,
  number,
  { rejectValue: { message: string }; state: RootState }
>("purchase/initiate", async (propertyId, thunkAPI) => {
  try {
    const token = thunkAPI.getState().auth.token;
    if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

    const response = await axios.post(
      `${API_URL}/purchases`,
      { property_id: propertyId },
      { headers: { Authorization: `Bearer ${token}` } }
    );

    const payload = response.data.purchase || response.data.data || response.data;

    return {
      bank: payload.bank ?? payload.bank_name,
      account_number: payload.account_number,
      account_name: payload.account_name,
      amount: payload.amount ?? payload.price,
      reference: payload.reference,
      accountExpiry: payload.accountExpiry || payload.account_expiry,
    };
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      error.response?.data || { message: "Failed to initiate purchase" }
    );
  }
});

export const checkPurchaseStatus = createAsyncThunk<
  { statusText?: string },
  string,
  { rejectValue: { message: string }; state: RootState }
>("purchase/checkStatus", async (reference, thunkAPI) => {
  try {
    const token = thunkAPI.getState().auth.token;
    if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

    const response = await axios.get(`${API_URL}/transactions/${reference}`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    const data = response.data?.data || response.data;
    return { statusText: data?.status_text || data?.status || "" };
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      error.response?.data || { message: "Failed to check purchase status" }
    );
  }
});

const purchaseSlice = createSlice({
  name: "purchase",
  initialState,
  reducers: {
    setPaymentDetails: (state, action) => {
      state.paymentDetails = action.payload;
    },
    setTransactionStatus: (state, action) => {
      state.transactionStatus = action.payload;
    },
    clearPurchaseError: (state) => {
      state.error = null;
    },
    resetPurchaseState: (state) => {
      state.loading = false;
      state.error = null;
      state.paymentDetails = null;
      state.transactionStatus = null;
      state.polling = false;
    },
    startPolling: (state) => {
      state.polling = true;
    },
    stopPolling: (state) => {
      state.polling = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(initiatePurchase.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(initiatePurchase.fulfilled, (state, action) => {
        state.loading = false;
        state.paymentDetails = action.payload;
        state.transactionStatus = null;
      })
      .addCase(initiatePurchase.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as { message?: string })?.message ||
          "Failed to initiate purchase";
      })
      .addCase(checkPurchaseStatus.pending, (state) => {
        state.polling = true;
      })
      .addCase(checkPurchaseStatus.fulfilled, (state, action) => {
        state.polling = false;
        state.transactionStatus = action.payload.statusText || null;
      })
      .addCase(checkPurchaseStatus.rejected, (state, action) => {
        state.polling = false;
        state.error =
          (action.payload as { message?: string })?.message ||
          "Failed to check purchase status";
      });
  },
});

export const { resetPurchaseState, startPolling, stopPolling } =
  purchaseSlice.actions;
export const {
  setPaymentDetails,
  setTransactionStatus,
  clearPurchaseError,
} = purchaseSlice.actions;
export default purchaseSlice.reducer;

// Selectors
export const selectTransferPayment = (state: RootState) => state.transferPayment;
